import { siteConfig } from "@acme/config";
import { createFileRoute, Link } from "@tanstack/react-router";
import { Disclosure } from "~/components/inside/disclosure";
import { InnerCta } from "~/components/inside/inner-cta";
import { PageIntro, PageLabel } from "~/components/inside/page-intro";
import { PageScene } from "~/components/inside/page-scene";
import { QuietLink } from "~/components/inside/quiet-link";
import { ReadLayout } from "~/components/inside/read-layout";
import { SideIndex } from "~/components/inside/side-index";
import { usePageEngagement } from "~/hooks/use-page-engagement";
import { publicCacheHeaders } from "~/lib/cache-headers";
import { getFaqCategories, getFaqItems } from "~/lib/constants/faq";
import { seo } from "~/lib/seo";
import {
  generateStructuredDataGraph,
  getFaqPageSchemas,
} from "~/lib/structured-data";
import * as m from "~/paraglide/messages.js";

export const Route = createFileRoute("/(public)/faq")({
  component: FaqPage,
  head: () => {
    const seoData = seo({
      description: m.meta_faq_description(),
      keywords:
        "AI automation FAQ, custom AI agents, RAG development, n8n automation, AI integration, AI automation cost, human review, Lindevo",
      path: "/faq",
      title: m.meta_faq_title(),
    });
    const structuredData = generateStructuredDataGraph(
      getFaqPageSchemas(getFaqItems())
    );
    return {
      links: seoData.links,
      meta: seoData.meta,
      scripts: [
        {
          children: structuredData,
          type: "application/ld+json",
        },
      ],
    };
  },
  headers: publicCacheHeaders,
});

/**
 * The questions people ask before starting. Grouped by the worry behind them
 * rather than by topic, and answered without hedging — including the ones
 * whose honest answer is "that depends, and here is what it depends on".
 */
function FaqPage() {
  usePageEngagement({ pagePath: "/faq" });

  const categories = getFaqCategories();

  return (
    <>
      <PageScene variant="heading">
        <div>
          <PageLabel>{m.faq_label()}</PageLabel>
          <h1>{m.faq_title()}</h1>
          <PageIntro>{m.faq_intro()}</PageIntro>
        </div>

        <aside className="question-contact">
          <h2>{m.faq_contact_title()}</h2>
          <p>{m.faq_contact_body()}</p>
          <QuietLink href={`mailto:${siteConfig.contacts.mail}`}>
            {m.faq_contact_cta()}
          </QuietLink>
        </aside>
      </PageScene>

      <ReadLayout
        index={
          <SideIndex
            foot={
              <Link to="/how-we-work">
                {m.faq_index_direct()} <span aria-hidden="true">↗</span>
              </Link>
            }
            items={categories.map((category) => ({
              href: `#${category.id}`,
              label: category.category,
            }))}
            title={m.faq_index_title()}
          />
        }
      >
        {categories.map((category) => (
          <section className="faq-group" id={category.id} key={category.id}>
            <h2>{category.category}</h2>
            {category.items.map((item) => (
              <Disclosure
                defaultOpen={item.defaultOpen}
                id={item.id}
                key={item.id}
                summary={item.question}
              >
                <p>
                  {item.answer}{" "}
                  {item.link ? (
                    <Link hash={item.link.hash} to={item.link.to}>
                      {item.link.label}
                    </Link>
                  ) : null}
                </p>
              </Disclosure>
            ))}
          </section>
        ))}
      </ReadLayout>

      <InnerCta
        lede={m.faq_cta_lede()}
        note={m.faq_cta_note()}
        title={m.faq_cta_title()}
      />
    </>
  );
}
